import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import ChatRounded from "@mui/icons-material/ChatRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import Header from "../Header";
import Footer from "../Footer";
import { useCookies } from "react-cookie";
import Chat from "../../../api/Chat";
import User from "../../../api/User";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import EmptyListState from "../../EmptyListState";
import { FONTS } from "../../../constants/styles";
import { FullScreenLoader } from "../../FullScreenLoader";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const formatRelativeTime = (time: Dayjs) => {
    const now = dayjs();
    const minutes = now.diff(time, "minutes");
    const hours = now.diff(time, "hours");
    const days = now.diff(time, "days");
    if (days > 1) return time.format("DD.MM.YYYY");
    if (days === 1) return "Yesterday";
    if (hours >= 1) return `${hours}h ago`;
    if (minutes >= 1) return `${minutes}m ago`;
    return "Now";
};

const ChatsList = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [chats, setChats] = useState<Chat[]>([]);
    const [loading, setLoading] = useState(true);

    /* ── Single scroll (Pattern A) ── */
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollY, setScrollY] = useState(0);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setScrollY(el.scrollTop);
    }, []);

    useEffect(() => {
        if (!uuid || !brand_id) {
            window.location.href = "/";
            return;
        }
        setLoading(true);
        Chat.list(uuid, brand_id)
            .then((chats) =>
                setChats(
                    (chats ?? []).sort((a, b) =>
                        dayjs(b.created).diff(dayjs(a.created))
                    )
                )
            )
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id]);

    if (loading) return <FullScreenLoader />;

    const totalUnread = chats.reduce((sum, c) => sum + c.unread, 0);

    return (
        <>
            <Header scrollY={scrollY} />

            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden" }}
                bgcolor="white"
            >
                {/* ── Hero ── */}
                <Stack
                    width="100%"
                    sx={{
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        pt: "calc(72px + env(safe-area-inset-top))",
                        pb: "40px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: "16px",
                            px: 2,
                            py: 1.5,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Stack spacing={1}>
                            <Typography
                                fontFamily={FONTS.display}
                                fontWeight={700}
                                fontSize="28px"
                                lineHeight={1.2}
                                color="white"
                            >
                                Chats
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {chats.length > 0 && (
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(255,255,255,0.15)",
                                            borderRadius: "12px",
                                            px: 1.25,
                                            py: 0.25,
                                        }}
                                    >
                                        <Typography
                                            fontSize="12px"
                                            fontWeight={600}
                                            color="white"
                                        >
                                            {chats.length} conversation
                                            {chats.length !== 1 ? "s" : ""}
                                        </Typography>
                                    </Box>
                                )}
                                {totalUnread > 0 && (
                                    <Box
                                        sx={{
                                            bgcolor: "rgba(255,255,255,0.15)",
                                            borderRadius: "12px",
                                            px: 1.25,
                                            py: 0.25,
                                        }}
                                    >
                                        <Typography
                                            fontSize="12px"
                                            fontWeight={600}
                                            color="white"
                                        >
                                            {totalUnread} unread
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>

                {/* ── Bottom sheet ── */}
                <Stack
                    sx={{
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        bgcolor: "white",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ pt: 2.5, pb: 1 }} />

                    {/* ── List content ── */}
                    <Box>
                        {chats.length === 0 ? (
                            <EmptyListState
                                primaryText="No chats yet"
                                secondaryText="Start a conversation from a client's profile. Messages are auto-translated to each client's language."
                                icon={
                                    <ChatRounded
                                        sx={{
                                            fontSize: 28,
                                            color: primary,
                                            opacity: 0.5,
                                        }}
                                    />
                                }
                                button={{
                                    text: "Go to Clients",
                                    icon: (
                                        <PersonRounded
                                            sx={{
                                                fontSize: 18,
                                                color: primary,
                                            }}
                                        />
                                    ),
                                    onClick: () => navigate("/users"),
                                }}
                            />
                        ) : (
                            <Stack
                                sx={{
                                    px: 2,
                                    pb: "200px",
                                    "@keyframes fadeSlideIn": {
                                        from: {
                                            opacity: 0,
                                            transform: "translateY(12px)",
                                        },
                                        to: {
                                            opacity: 1,
                                            transform: "translateY(0)",
                                        },
                                    },
                                    animation: "fadeSlideIn 0.3s ease-out",
                                }}
                            >
                                {chats.map((chat, i) => (
                                    <React.Fragment key={chat.key}>
                                        <ChatRow
                                            uuid={uuid}
                                            brand_id={brand_id}
                                            chat={chat}
                                            index={i}
                                        />
                                        {i < chats.length - 1 && (
                                            <Box
                                                sx={{
                                                    height: "1px",
                                                    bgcolor: "rgba(0,0,0,0.06)",
                                                    ml: "60px",
                                                }}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </Stack>

            <Footer tab="chats" />
        </>
    );
};

/* ── Chat Row ── */

const ChatRow = (props: {
    uuid: string;
    brand_id: string;
    chat: Chat;
    index: number;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const { uuid, brand_id, chat, index } = props;
    const [user, setUser] = useState<User | undefined>();

    useEffect(() => {
        User.info(uuid, brand_id, chat.key).then(setUser).catch(console.error);
    }, [uuid, brand_id, chat.key]);

    if (!user) return null;

    const hasUnread = chat.unread > 0;

    const formatName = (name: string) => {
        const names = name.split(" ");
        return names.length > 1
            ? `${names[0]} ${names.slice(1).join(" ")}`
            : names[0];
    };

    return (
        <ButtonBase
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                py: 1.25,
                px: 0.5,
                borderRadius: "12px",
                "@keyframes fadeSlideUp": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `fadeSlideUp 0.25s ease-out ${index * 30}ms both`,
            }}
            onClick={() => navigate(`/chats/${chat.key}`)}
        >
            {/* Avatar */}
            {user.image ? (
                <Box
                    sx={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        borderRadius: "14px",
                        backgroundImage: `url(${user.image})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        flex: "0 0 auto",
                        position: "relative",
                    }}
                >
                    {hasUnread && <UnreadBadge count={chat.unread} />}
                </Box>
            ) : (
                <Box
                    sx={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        borderRadius: "14px",
                        bgcolor: alpha(primary, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        position: "relative",
                    }}
                >
                    <PersonRounded
                        sx={{ fontSize: 22, color: primary, opacity: 0.6 }}
                    />
                    {hasUnread && <UnreadBadge count={chat.unread} />}
                </Box>
            )}

            {/* Info */}
            <Stack
                flex={1}
                ml={1.5}
                spacing={0.25}
                sx={{ overflow: "hidden" }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography
                        className="no-translation"
                        fontWeight={hasUnread ? 600 : 500}
                        fontSize="15px"
                        lineHeight={1.3}
                        noWrap
                    >
                        {formatName(user.name)}
                    </Typography>
                    <Typography
                        fontSize="11px"
                        color="text.disabled"
                        flexShrink={0}
                        ml={1}
                    >
                        {formatRelativeTime(dayjs(chat.created))}
                    </Typography>
                </Stack>
                <Typography
                    fontSize="13px"
                    lineHeight="18px"
                    color={hasUnread ? "text.primary" : "text.secondary"}
                    fontWeight={hasUnread ? 500 : 400}
                    noWrap
                    textAlign="left"
                >
                    {chat.message}
                </Typography>
            </Stack>
        </ButtonBase>
    );
};

/* ── Unread Badge ── */

const UnreadBadge = ({ count }: { count: number }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                position: "absolute",
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                borderRadius: "9px",
                bgcolor: theme.palette.primary.main,
                border: "2px solid white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
            }}
        >
            <Typography
                fontSize="10px"
                fontWeight={700}
                color="white"
                lineHeight={1}
            >
                {count}
            </Typography>
        </Box>
    );
};

export default ChatsList;
